import { IconType } from "react-icons/lib";
import React from "react";
import styled, { ThemedStyledProps } from "styled-components";
import { accessibleShade } from "../accessibleColor";
import { layout, LayoutProps, space, SpaceProps } from "styled-system";

type ContainerProps = SpaceProps &
  LayoutProps & {
    image?: string | null;
    textColor?: string;
  };

/**
 * The circle's fill. Coloured circles hold white initials or icons, so the
 * colour is deepened just enough for them to read, the same shade buttons
 * and links use. Light greys and soft tints (e.g. "warningSoft", used with a
 * dark textColor) stay as they are.
 */
const circleColor = (
  props: ThemedStyledProps<ContainerProps & { color?: string }, any>,
) => {
  const name = props.color || "grey3";
  const color = (props.theme.colors[name] || name) as string;
  return /^(grey\d?|bg\w*|white|\w+Soft)$/.test(name)
    ? color
    : accessibleShade(color);
};

const Container = styled.div<ContainerProps>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: ${circleColor};
  background-image: url("${(props) => props.image}");
  background-size: cover;
  background-position: center;
  color: ${(props) =>
    props.textColor
      ? (props.theme.colors[props.textColor] ?? props.textColor)
      : "white"};
  flex: 0 0 auto;
  cursor: ${(props) => (props.onClick != null ? "pointer" : undefined)};
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(${(props) => (props.onClick != null ? 1.2 : 1)});
  }
  ${space};
  ${layout};
`;

export type CircleProps = {
  icon?: IconType | null;
  size?: "tiny" | "small" | "medium" | "large" | "xlarge" | "xxlarge" | number;
  color?: "primary" | "blue" | string;
  /** Icon or text colour. White by default; set it for a soft background. */
  textColor?: string;
  image?: string | null;
  children?: React.ReactNode;
  style?: React.CSSProperties;
  title?: string;
  id?: string;
} & SpaceProps &
  Omit<React.ComponentPropsWithoutRef<"div">, "size">;

const sizes = {
  tiny: 18,
  small: 24,
  medium: 32,
  large: 48,
  xlarge: 72,
  xxlarge: 120,
} as const;

const iconSizes = {
  tiny: "0.6em",
  small: "0.9em",
  medium: "1.2em",
  large: "1.7em",
  xlarge: "2em",
  xxlarge: "3em",
} as const;

const Circle = ({
  size = "medium",
  icon: Icon = null,
  color = "grey3",
  image = null,
  id,
  title,
  children,
  ...rest
}: CircleProps) => {
  const width: number = typeof size === "number" ? size : sizes[size];
  const iconSize: string =
    typeof size === "number" ? `${size / 2}px` : iconSizes[size];

  return (
    <Container
      id={id}
      width={width}
      height={width}
      color={color}
      image={image}
      title={title}
      {...rest}
    >
      {Icon && <Icon size={iconSize} />}
      {children}
    </Container>
  );
};

export default Circle;
